import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import api from "../../services/api";
import { 
  ArrowLeft, Edit, Briefcase, MapPin, Calendar, DollarSign, Building2, 
  Clock, CheckCircle2, Users, GraduationCap, MessageSquare 
} from "lucide-react";
import toast from "react-hot-toast";

const formatDate = (value) => value ? new Date(value).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" }) : "";

export default function JobView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const token = localStorage.getItem("admin_token");
        const response = await api.get(`/jobs/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setJob(response.data);
      } catch (error) {
        console.error("Error:", error);
        toast.error("Failed to load job details");
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [id]);

  const getParsedArray = (data) => {
    if (!data) return [];
    if (typeof data === 'string') {
      try { return JSON.parse(data); } catch { return []; }
    }
    return data;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-[70vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!job) return <AdminLayout><div className="p-8">Job not found</div></AdminLayout>;

  return (
    <AdminLayout>
      {/* Top Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-gray-500 hover:text-black mb-3 transition-colors">
            <ArrowLeft size={16} /> Back to Jobs
          </button>
          <div className="flex items-center gap-4">
            <div className="p-4 bg-yellow-100 text-yellow-700 rounded-2xl shadow-sm hidden sm:block">
              <Briefcase size={32} />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">{job.title}</h1>
              <div className="flex items-center gap-3 mt-2 text-sm text-gray-500 font-medium">
                <span className="flex items-center gap-1"><Building2 size={16}/> {job.employer ? job.employer.company_name : "Kadtech Admin"}</span>
                <span>•</span>
                <span className="flex items-center gap-1 text-blue-700 bg-blue-50 px-2.5 py-1 rounded-lg border border-blue-100">{job.category}</span>
                <span className="flex items-center gap-1 text-gray-700 bg-gray-100 px-2.5 py-1 rounded-lg border border-gray-200">{job.status}</span>
              </div>
            </div>
          </div>
        </div>
        <button 
          onClick={() => navigate(`/jobs/edit/${job.id}`)} 
          className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-6 rounded-xl transition-colors shadow-sm"
        >
          <Edit size={18} /> Edit Job
        </button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
          <span className="flex items-center gap-1.5 text-xs text-gray-500 font-bold uppercase tracking-wider mb-2"><MapPin size={16}/> Location</span>
          <span className="text-sm font-bold text-gray-900 block truncate">{job.location || "Not Specified"}</span>
          <span className="text-xs text-gray-500 mt-1 block">{job.work_location_type || "Work from office"}</span>
        </div>
        <div className="bg-green-50 p-5 rounded-2xl border border-green-100 shadow-sm">
          <span className="flex items-center gap-1.5 text-xs text-green-700 font-bold uppercase tracking-wider mb-2"><DollarSign size={16}/> Salary</span>
          <span className="text-sm font-bold text-green-900 block truncate">{job.salary_range || "Not Disclosed"}</span>
          <span className="text-xs text-green-700 mt-1 block">{job.contractual ? "Contractual" : "Regular Payroll"}</span>
        </div>
        <div className="bg-blue-50 p-5 rounded-2xl border border-blue-100 shadow-sm">
          <span className="flex items-center gap-1.5 text-xs text-blue-700 font-bold uppercase tracking-wider mb-2"><Clock size={16}/> Job Type</span>
          <span className="text-sm font-bold text-blue-900 block">{job.job_type || "Full-Time"}</span>
          <span className="text-xs text-blue-700 mt-1 block">{job.shift || "Day"} Shift</span>
        </div>
        <div className="bg-orange-50 p-5 rounded-2xl border border-orange-100 shadow-sm">
          <span className="flex items-center gap-1.5 text-xs text-orange-700 font-bold uppercase tracking-wider mb-2"><Calendar size={16}/> Deadline</span>
          <span className="text-sm font-bold text-orange-900 block">{formatDate(job.deadline)}</span>
          <span className="text-xs text-orange-700 mt-1 block">{job.vacancies} Vacancies</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Content Area */}
        <div className="lg:col-span-2 space-y-8">
          <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-200 shadow-sm">
            <h2 className="text-xl font-extrabold text-gray-900 mb-6 border-b border-gray-100 pb-4">Job Description</h2>
            <div className="prose max-w-none text-gray-700 text-base leading-loose whitespace-pre-wrap">
              {job.description}
            </div>
          </div>

          {getParsedArray(job.benefits).length > 0 && (
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-200 shadow-sm">
              <h2 className="text-xl font-extrabold text-gray-900 mb-6 border-b border-gray-100 pb-4">Perks & Benefits</h2>
              <div className="flex flex-wrap gap-3">
                {getParsedArray(job.benefits).map((ben, index) => (
                  <span key={index} className="px-4 py-2 bg-teal-50 text-teal-800 border border-teal-200 rounded-xl text-sm font-bold flex items-center gap-2">
                    <CheckCircle2 size={18} /> {ben}
                  </span>
                ))}
              </div>
            </div>
          )}

          {job.interview_type && (
            <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-200 shadow-sm">
              <h2 className="text-xl font-extrabold text-gray-900 mb-6 border-b border-gray-100 pb-4 flex items-center gap-2">
                <Users size={22} className="text-blue-600"/> Interview Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 bg-gray-50 p-6 rounded-xl border border-gray-100">
                <div><span className="text-gray-500 block text-xs font-bold uppercase mb-1">Interview Type</span><span className="font-bold text-gray-900 text-lg">{job.interview_type}</span></div>
                <div><span className="text-gray-500 block text-xs font-bold uppercase mb-1">Deposit Required?</span><span className="font-bold text-gray-900 text-lg">{job.deposit_required || "No"}</span></div>
                {job.interview_type === "In-person" && (
                  <div className="sm:col-span-2 mt-2">
                    <span className="text-gray-500 block text-xs font-bold uppercase mb-1">Interview Address</span>
                    <span className="font-medium text-gray-800 text-base">{job.interview_locality}, {job.interview_city}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
            <h3 className="font-extrabold text-gray-900 mb-6 text-lg border-b border-gray-100 pb-3">Candidate Requirements</h3>
            <ul className="space-y-5">
              <li className="flex items-start gap-4">
                <div className="p-2.5 bg-gray-50 text-gray-600 rounded-lg border border-gray-100"><GraduationCap size={20} /></div>
                <div>
                  <span className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-0.5">Education</span>
                  <span className="text-sm font-bold text-gray-900">{job.qualification || job.course_education || "Not specified"}</span>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="p-2.5 bg-gray-50 text-gray-600 rounded-lg border border-gray-100"><Briefcase size={20} /></div>
                <div>
                  <span className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-0.5">Experience</span>
                  <span className="text-sm font-bold text-gray-900">{job.freshers_only ? "Fresher Only" : (job.experience || "Not specified")}</span>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="p-2.5 bg-gray-50 text-gray-600 rounded-lg border border-gray-100"><Users size={20} /></div>
                <div>
                  <span className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-0.5">Gender Preference</span>
                  <span className="text-sm font-bold text-gray-900">{job.gender || "Any"}</span>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="p-2.5 bg-gray-50 text-gray-600 rounded-lg border border-gray-100"><MessageSquare size={20} /></div>
                <div>
                  <span className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-0.5">English Level</span>
                  <span className="text-sm font-bold text-gray-900">{job.english_fluency || "Not specified"}</span>
                </div>
              </li>
            </ul>
          </div>

          {getParsedArray(job.skills).length > 0 && (
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="font-extrabold text-gray-900 mb-5 text-lg border-b border-gray-100 pb-3">Required Skills</h3>
              <div className="flex flex-wrap gap-2">
                {getParsedArray(job.skills).map((skill, index) => (
                  <span key={index} className="px-3 py-1.5 bg-gray-50 text-gray-800 border border-gray-200 rounded-lg text-sm font-bold">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}